import {ProposalActivitySourcePicker} from './ProposalActivitySourcePicker';
import type {ProposalActivitySource} from '@/services/ResearchService';
import React, { useState, useEffect, useRef } from 'react';
import {
  MapPin,
  Image as ImageIcon,
  Plus,
  Trash2,
  GripVertical,
  Sparkles,
  Check,
  X,
  AlertCircle,
  Upload,
  Loader2,
  Edit3,
  Clock,
  DollarSign,
  Info,
  FileImage,
  Activity,
  Video,
  Calendar,
  Users,
  Search,
  ExternalLink,
  Database,
  ArrowUp,
  Cloud,
  ThermometerSun,
  BookmarkPlus,
  RotateCcw,
  Trash
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge/Badge';
import { Card, CardContent } from '@/components/ui/Card/Card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useNotificationStore } from '@/stores/notificationStore';
import {
  proposalService,
  type DestinationContent,
  type ImageObject,
  type DestinationActivity,
  type ActivityCategory,
  type ActivityIntensity,
  type CreateActivityInput,
  type UpdateActivityInput,
  type DestinationEvent,
  type EventTag,
  type CreateEventInput,
  type UpdateEventInput,
  type ResearchDestinationWeather,
  type UpsertResearchWeatherInput,
} from '@/services/ProposalService';
import { getTravelMonthForWeather } from '@/lib/dateFormatters';
import type { EventDTO } from '@/services/EventsService';
import { AttachmentsService } from '@/services/AttachmentsService';
import { UnsplashImageSearch } from '@/components/proposal/UnsplashImageSearch';
import { EventLibraryBrowser } from '@/components/proposal/EventLibraryBrowser';
import { ActivityLibraryBrowser } from '@/components/proposal/ActivityLibraryBrowser';

// Activity category options
const CATEGORY_OPTIONS: { value: ActivityCategory; label: string; emoji: string }[] = [
  { value: 'culture', label: 'Culture', emoji: '🏛️' },
  { value: 'adventure', label: 'Adventure', emoji: '🏔️' },
  { value: 'food_drink', label: 'Food & Drink', emoji: '🍷' },
  { value: 'wellness', label: 'Wellness', emoji: '🧘' },
  { value: 'entertainment', label: 'Entertainment', emoji: '🎭' },
  { value: 'nature', label: 'Nature', emoji: '🌿' },
  { value: 'team_building', label: 'Team Building', emoji: '🤝' },
  { value: 'sightseeing', label: 'Sightseeing', emoji: '📸' },
  { value: 'water_sports', label: 'Water Sports', emoji: '🏄' },
  { value: 'other', label: 'Other', emoji: '✨' },
];

// Intensity options
const INTENSITY_OPTIONS: { value: ActivityIntensity; label: string; color: string }[] = [
  { value: 'relaxed', label: 'Relaxed', color: 'text-success-600' },
  { value: 'moderate', label: 'Moderate', color: 'text-warning-600' },
  { value: 'active', label: 'Active', color: 'text-error-600' },
];

// Event tag options
const TAG_OPTIONS: { value: EventTag; label: string; emoji: string }[] = [
  { value: 'off_venue', label: 'Offsite Venue', emoji: '🏛️' },
  { value: 'welcome_reception', label: 'Welcome Reception', emoji: '🥂' },
  { value: 'final_dinner', label: 'Final Dinner', emoji: '🍽️' },
  { value: 'nightlife', label: 'Nightlife', emoji: '🌙' },
];

interface DestinationEditorModalProps {
  activitySourceOptions?: ProposalActivitySource[];
  onActivitySourceChange?: (id:string)=>Promise<void>;
  isOpen: boolean;
  onClose: () => void;
  city: string;
  country: string;
  existingContent?: DestinationContent | null;
  onSave?: (content: DestinationContent) => void;
  onActivitiesChanged?: () => void;
  /**
   * If provided, the modal will edit proposal-specific activities.
   * If not provided, the modal will edit global activities.
   */
  researchDestinationId?: string;
  /** Activity-list changes must not switch the group’s weather record. */
  weatherDestinationId?: string;
  /**
   * Event data for calculating travel month for weather research
   */
  eventData?: EventDTO | null;
}

export function DestinationEditorModal({
  isOpen,
  onClose,
  city,
  country,
  existingContent,
  onSave,
  onActivitiesChanged,
  researchDestinationId,
  weatherDestinationId = researchDestinationId,
  eventData,
  activitySourceOptions = [],
  onActivitySourceChange,
}: DestinationEditorModalProps) {
  const notificationStore = useNotificationStore();
  const [activeTab, setActiveTab] = useState('general');
  
  // Content form state
  const [shortDescription, setShortDescription] = useState('');
  const [keyPros, setKeyPros] = useState<string[]>([]);
  const [considerations, setConsiderations] = useState<string[]>([]);
  const [images, setImages] = useState<ImageObject[]>([]);
  const [video, setVideo] = useState<{ url: string; attachment_id?: string; thumbnail?: string } | null>(null);
  const [mediaLayout, setMediaLayout] = useState<'gallery' | 'grid' | 'video' | null>(null);

  const [isResearchingContent, setIsResearchingContent] = useState(false);
  
  // Activities state - can be either global or proposal-specific
  const [activities, setActivities] = useState<DestinationActivity[]>([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  // Map of activity ID to global_activity_id (for proposal-specific activities)
  const [activityGlobalIdMap, setActivityGlobalIdMap] = useState<Map<string, string | null>>(new Map());
  
  // Destination activities (city/country pool — for viewing/copying in proposal context)
  const [destinationActivities, setDestinationActivities] = useState<DestinationActivity[]>([]);
  const [isLoadingDestinationActivities, setIsLoadingDestinationActivities] = useState(false);
  const [showDestinationActivities, setShowDestinationActivities] = useState(false);
  const [promotingActivityId, setPromotingActivityId] = useState<string | null>(null);
  const [copyingActivityIds, setCopyingActivityIds] = useState<Set<string>>(new Set());
  // Activity library browser (org-wide universal activities)
  const [showActivityLibraryBrowser, setShowActivityLibraryBrowser] = useState(false);
  const [savingToLibraryId, setSavingToLibraryId] = useState<string | null>(null);
  const [savingEventToLibraryId, setSavingEventToLibraryId] = useState<string | null>(null);
  
  // Determine if we're in proposal context (editing proposal-specific activities)
  const isProposalContext = !!researchDestinationId || !!onActivitySourceChange;
  
  // Activity edit form state
  const [editingActivity, setEditingActivity] = useState<DestinationActivity | null>(null);
  const [isCreatingActivity, setIsCreatingActivity] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ActivityCategory | ''>('');
  const [intensity, setIntensity] = useState<ActivityIntensity | ''>('');
  const [durationHours, setDurationHours] = useState('');
  const [price, setPrice] = useState('');
  const [activityImage, setActivityImage] = useState<ImageObject | null>(null);
  
  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [isUnsplashSearchOpen, setIsUnsplashSearchOpen] = useState(false);
  const [isSavingActivity, setIsSavingActivity] = useState(false);
  const [isUploadingActivity, setIsUploadingActivity] = useState(false);
  const [isReorderingActivities, setIsReorderingActivities] = useState(false);
  const [deletingActivityId, setDeletingActivityId] = useState<string | null>(null);
  const [restoringActivityId, setRestoringActivityId] = useState<string | null>(null);
  const [hardDeletingActivityId, setHardDeletingActivityId] = useState<string | null>(null);
  const [hardDeleteActivityConfirm, setHardDeleteActivityConfirm] = useState<DestinationActivity | null>(null);
  const [showArchivedActivities, setShowArchivedActivities] = useState(false);

  // Events state
  const [events, setEvents] = useState<DestinationEvent[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [editingEvent, setEditingEvent] = useState<DestinationEvent | null>(null);
  const [isCreatingEvent, setIsCreatingEvent] = useState(false);
  const [isSavingEvent, setIsSavingEvent] = useState(false);
  const [isUploadingEventImage, setIsUploadingEventImage] = useState(false);
  const [isReorderingEvents, setIsReorderingEvents] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [restoringEventId, setRestoringEventId] = useState<string | null>(null);
  const [hardDeletingEventId, setHardDeletingEventId] = useState<string | null>(null);
  const [hardDeleteEventConfirm, setHardDeleteEventConfirm] = useState<DestinationEvent | null>(null);
  const [showArchivedEvents, setShowArchivedEvents] = useState(false);
  const [eventLibraryOpen, setEventLibraryOpen] = useState(false);

  // Weather state (only for proposal context)
  // Initialize with empty structure so form is always visible
  const [weather, setWeather] = useState<ResearchDestinationWeather | null>(null);
  const [isLoadingWeather, setIsLoadingWeather] = useState(false);
  const [isResearchingWeather, setIsResearchingWeather] = useState(false);
  const [isSavingWeather, setIsSavingWeather] = useState(false);
  const [isAnnualDataPending, setIsAnnualDataPending] = useState(false);
  
  // Initialize empty weather structure if needed
  const getWeatherData = (): ResearchDestinationWeather | null => {
    if (weather) return weather;
    // Return null - we'll handle empty state in the form
    return null;
  };
  
  // Helper to get or initialize annual climate data
  const getAnnualClimateData = (): Array<{ month: string; avg_high: number; avg_low: number; rainfall: number }> => {
    if (weather?.annual_climate_data && weather.annual_climate_data.length === 12) {
      return weather.annual_climate_data;
    }
    // Return empty 12-month structure
    return [
      { month: 'Jan', avg_high: 0, avg_low: 0, rainfall: 0 },
      { month: 'Feb', avg_high: 0, avg_low: 0, rainfall: 0 },
      { month: 'Mar', avg_high: 0, avg_low: 0, rainfall: 0 },
      { month: 'Apr', avg_high: 0, avg_low: 0, rainfall: 0 },
      { month: 'May', avg_high: 0, avg_low: 0, rainfall: 0 },
      { month: 'Jun', avg_high: 0, avg_low: 0, rainfall: 0 },
      { month: 'Jul', avg_high: 0, avg_low: 0, rainfall: 0 },
      { month: 'Aug', avg_high: 0, avg_low: 0, rainfall: 0 },
      { month: 'Sep', avg_high: 0, avg_low: 0, rainfall: 0 },
      { month: 'Oct', avg_high: 0, avg_low: 0, rainfall: 0 },
      { month: 'Nov', avg_high: 0, avg_low: 0, rainfall: 0 },
      { month: 'Dec', avg_high: 0, avg_low: 0, rainfall: 0 },
    ];
  };

  // Helper to update annual climate data
  const updateAnnualClimateData = (index: number, field: 'avg_high' | 'avg_low' | 'rainfall', value: number) => {
    const weatherData = getWeatherData();
    const annualData = getAnnualClimateData();
    const updatedAnnualData = [...annualData];
    updatedAnnualData[index] = {
      ...updatedAnnualData[index],
      [field]: value,
    };
    
    setWeather({
      ...(weatherData || {
        id: '',
        research_destination_id: weatherDestinationId || '',
        day_avg_temperature: null,
        night_avg_temperature: null,
        rainfall: null,
        rainy_days: null,
        humidity: null,
        weather_description: null,
        travel_tip_weather: null,
        travel_tip_packing: null,
        travel_tip_local_events: null,
        annual_climate_data: [],
        travel_month: null,
        researched_at: null,
        llm_log_id: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }),
      annual_climate_data: updatedAnnualData,
    });
  };
  
  // Event form fields
  const [eventName, setEventName] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventTag, setEventTag] = useState<EventTag | ''>('');
  const [eventPrice, setEventPrice] = useState('');
  const [eventParticipants, setEventParticipants] = useState('');
  const [eventImage, setEventImage] = useState<ImageObject | null>(null);
  const eventFileInputRef = useRef<HTMLInputElement>(null);
  
  const [newPro, setNewPro] = useState('');
  const [newConsideration, setNewConsideration] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const activityFileInputRef = useRef<HTMLInputElement>(null);
  const attachmentsService = new AttachmentsService();
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Load existing content when opening
  useEffect(() => {
    if (isOpen) {
      if (existingContent) {
        setShortDescription(existingContent.short_description || '');
        setKeyPros(Array.isArray(existingContent.key_pros) ? existingContent.key_pros : []);
        setConsiderations(Array.isArray(existingContent.considerations) ? existingContent.considerations : []);
        const sortedImages = (existingContent.images || []).sort((a, b) => {
          const orderA = a.sort_order ?? 0;
          const orderB = b.sort_order ?? 0;
          return orderA - orderB;
        });
        setImages(sortedImages);
        setVideo(existingContent.video || null);
        setMediaLayout(existingContent.media_layout || null);
      } else {
        setShortDescription('');
        setKeyPros([]);
        setConsiderations([]);
        setImages([]);
        setVideo(null);
        setMediaLayout(null);
      }

      loadActivities();
      loadEvents();
      
      // Load destination-pool activities and weather if in proposal context
      if (isProposalContext) {
        loadDestinationActivities();
        loadWeather();
      }
    }
  }, [isOpen, existingContent, city, country, isProposalContext, researchDestinationId, weatherDestinationId]);

  // AI populate destination content (description, pros, considerations)
  async function handlePopulateContent() {
    try {
      setIsResearchingContent(true);
      
      const result = await proposalService.researchDestinationContent(city, country);
      
      // Populate fields from AI result
      setShortDescription(result.content.short_description);
      setKeyPros(result.content.key_pros);
      setConsiderations(result.content.considerations);
      
      // Auto-save content to database
      const contentPayload: any = {
        city,
        country,
        short_description: result.content.short_description || null,
        key_pros: result.content.key_pros || [],
        considerations: result.content.considerations || [],
      };
      
      const savedContent = await proposalService.upsertDestinationContent(contentPayload);
      onSave?.(savedContent);
      
      notificationStore.success('Destination content populated and saved successfully', { title: 'AI Research Complete' });
    } catch (error: any) {
      console.error('Failed to research destination content:', error);
      notificationStore.error(error.message || 'Failed to populate destination content', { title: 'AI Research Error' });
    } finally {
      setIsResearchingContent(false);
    }
  }

  async function loadActivities() {
    try {
      setIsLoadingActivities(true);
      if (isProposalContext && !researchDestinationId) { setActivities([]); return; }
      
      if (isProposalContext && researchDestinationId) {
        // Load proposal-specific activities (include excluded so users can restore them)
        const items = await proposalService.listResearchActivities(researchDestinationId, true);
        // Build map of activity ID to global_activity_id
        const globalIdMap = new Map<string, string | null>();
        items.forEach(item => {
          globalIdMap.set(item.id, item.global_activity_id);
        });
        setActivityGlobalIdMap(globalIdMap);

        // Convert ResearchDestinationActivity to DestinationActivity format for display.
        // Map is_included -> is_active so the "Archived" badge and restore UI work consistently.
        const convertedItems: DestinationActivity[] = items.map(item => ({
          id: item.id,
          created_by_organization_id: null,
          city,
          country,
          name: item.name,
          description: item.description,
          category: item.category,
          intensity: item.intensity,
          duration_hours: item.duration_hours,
          estimated_price_per_person: item.estimated_price_per_person,
          currency_code: item.currency_code,
          images: item.images,
          sort_order: item.sort_order,
          is_active: item.is_included, // Map is_included to is_active for display
          last_updated_by_user_id: null,
          created_at: item.created_at,
          updated_at: item.updated_at,
        }));
        setActivities(convertedItems);
      } else {
        // Load global activities (include inactive so users can restore archived ones)
        const items = await proposalService.listActivities(city, country, true);
        setActivities(items);
      }
    } catch (error) {
      console.error('Failed to load activities:', error);
      notificationStore.error('Failed to load activities', { title: 'Load Error' });
    } finally {
      setIsLoadingActivities(false);
    }
  }
  
  async function loadDestinationActivities() {
    if (!isProposalContext) return;

    try {
      setIsLoadingDestinationActivities(true);
      const items = await proposalService.listActivities(city, country, false);
      setDestinationActivities(items.filter(a => a.is_active));
    } catch (error) {
      console.error('Failed to load destination activities:', error);
    } finally {
      setIsLoadingDestinationActivities(false);
    }
  }

  async function handlePromoteToDestination(activityId: string) {
    if (!researchDestinationId) return;

    try {
      setPromotingActivityId(activityId);
      await proposalService.promoteActivityToGlobal(researchDestinationId, activityId, city, country);
      notificationStore.success('Activity saved to destination pool', { title: 'Success' });
      // Reload activities to update the global_activity_id map (promoted activity now has a link)
      await loadActivities();
      await loadDestinationActivities(); // Refresh destination activities list
      onActivitiesChanged?.();
    } catch (error: any) {
      console.error('Failed to save activity to destination:', error);
      notificationStore.error(error?.message || 'Failed to save activity to destination pool', { title: 'Error' });
    } finally {
      setPromotingActivityId(null);
    }
  }

  async function handleSaveToLibrary(researchActivityId: string) {
    try {
      setSavingToLibraryId(researchActivityId);
      await proposalService.promoteResearchActivityToLibrary(researchActivityId);
      notificationStore.success('Activity saved to global library', { title: 'Success' });
    } catch (error: any) {
      console.error('Failed to save activity to library:', error);
      notificationStore.error(error?.message || 'Failed to save activity to library', { title: 'Error' });
    } finally {
      setSavingToLibraryId(null);
    }
  }

  async function handleSaveEventToLibrary(eventId: string) {
    try {
      setSavingEventToLibraryId(eventId);
      await proposalService.promoteEventToLibrary(eventId);
      notificationStore.success('Event saved to global library', { title: 'Success' });
    } catch (error: any) {
      console.error('Failed to save event to library:', error);
      notificationStore.error(error?.message || 'Failed to save event to library', { title: 'Error' });
    } finally {
      setSavingEventToLibraryId(null);
    }
  }

  async function handleCopyDestinationActivity(globalActivityId: string) {
    if (!researchDestinationId) return;

    try {
      setCopyingActivityIds(prev => new Set(prev).add(globalActivityId));
      await proposalService.copyGlobalActivitiesToResearch(researchDestinationId, [globalActivityId]);
      notificationStore.success('Activity copied from destination pool', { title: 'Success' });
      // Reload activities to update the global_activity_id map
      await loadActivities();
      onActivitiesChanged?.();
    } catch (error: any) {
      console.error('Failed to copy activity:', error);
      notificationStore.error(error?.message || 'Failed to copy activity', { title: 'Error' });
    } finally {
      setCopyingActivityIds(prev => {
        const next = new Set(prev);
        next.delete(globalActivityId);
        return next;
      });
    }
  }
  
  async function handleSave() {
    try {
      setIsSaving(true);
      
      const payload: any = {
        city,
        country,
      };
      
      if (shortDescription.trim()) {
        payload.short_description = shortDescription.trim();
      }
      
      payload.key_pros = keyPros;
      payload.considerations = considerations;
      
      if (images.length > 0) {
        payload.images = images;
      }
      
      if (video) {
        payload.video = video;
      } else {
        payload.video = null;
      }
      
      payload.media_layout = mediaLayout || null;

      const content = await proposalService.upsertDestinationContent(payload);
      
      notificationStore.success('Destination content saved', { title: 'Success' });
      onSave?.(content);
    } catch (error) {
      console.error('Failed to save destination content:', error);
      notificationStore.error('Failed to save destination content', { title: 'Save Error' });
    } finally {
      setIsSaving(false);
    }
  }
  
  function addPro() {
    if (newPro.trim() && keyPros.length < 10) {
      setKeyPros([...keyPros, newPro.trim()]);
      setNewPro('');
    }
  }
  
  function removePro(index: number) {
    setKeyPros(keyPros.filter((_, i) => i !== index));
  }
  
  function addConsideration() {
    if (newConsideration.trim() && considerations.length < 10) {
      setConsiderations([...considerations, newConsideration.trim()]);
      setNewConsideration('');
    }
  }
  
  function removeConsideration(index: number) {
    setConsiderations(considerations.filter((_, i) => i !== index));
  }

  function removeImage(index: number) {
    setImages(images.filter((_, i) => i !== index).map((img, i) => ({ ...img, sort_order: i })));
  }
  
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setImages((items) => {
        const oldIndex = items.findIndex((item) => getImageId(item) === active.id);
        const newIndex = items.findIndex((item) => getImageId(item) === over.id);
        
        if (oldIndex === -1 || newIndex === -1) return items;
        
        const newItems = arrayMove(items, oldIndex, newIndex);
        return newItems.map((img, index) => ({ ...img, sort_order: index }));
      });
    }
  }
  
  async function handleActivitiesDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const oldIndex = activities.findIndex((activity) => activity.id === active.id);
    const newIndex = activities.findIndex((activity) => activity.id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    // Optimistically update the UI
    const reorderedActivities = arrayMove(activities, oldIndex, newIndex);
    setActivities(reorderedActivities);
    
    // Update sort_order in database
    try {
      setIsReorderingActivities(true);
      
      // Update all activities with their new sort_order
      const updatePromises = reorderedActivities.map((activity, index) => {
        if (isProposalContext && researchDestinationId) {
          return proposalService.updateResearchActivity(researchDestinationId, activity.id, { sort_order: index });
        } else {
          return proposalService.updateActivity(activity.id, { sort_order: index });
        }
      });
      
      await Promise.all(updatePromises);
      notificationStore.success('Activity order updated', { title: 'Success' });
    } catch (error) {
      console.error('Failed to update activity order:', error);
      notificationStore.error('Failed to update activity order', { title: 'Error' });
      // Reload activities to revert optimistic update
      await loadActivities();
    } finally {
      setIsReorderingActivities(false);
    }
  }
  
  function getImageId(image: ImageObject): string {
    return image.attachment_id || `url-${image.url}`;
  }
  
  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const currentCount = images.length;
    const maxImages = 5;
    
    if (currentCount >= maxImages) {
      notificationStore.error(`Maximum of ${maxImages} images allowed per destination`, { title: 'Upload Limit' });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    setIsUploading(true);
    try {
      const uploadedImages: ImageObject[] = [];
      const filesToProcess = Array.from(files).slice(0, maxImages - currentCount);
      
      if (files.length > filesToProcess.length) {
        notificationStore.warning(`Only ${filesToProcess.length} image${filesToProcess.length > 1 ? 's' : ''} will be uploaded (limit: ${maxImages})`, { title: 'Upload Limit' });
      }
      
      for (const file of filesToProcess) {
        if (!file.type.startsWith('image/')) {
          notificationStore.error(`${file.name} is not an image file`, { title: 'Upload Error' });
          continue;
        }
        
        try {
          const attachment = await attachmentsService.upload(file, {
            title: file.name.replace(/\.[^.]+$/, ''),
            tags: [`destination:${city}`, `country:${country}`],
          });
          
          uploadedImages.push({
            url: attachment.url,
            attachment_id: attachment.id,
            sort_order: images.length + uploadedImages.length,
          });
        } catch (error: any) {
          console.error(`Failed to upload ${file.name}:`, error);
          notificationStore.error(`Failed to upload ${file.name}`, { title: 'Upload Error' });
        }
      }
      
      if (uploadedImages.length > 0) {
        setImages([...images, ...uploadedImages]);
        notificationStore.success(`Successfully uploaded ${uploadedImages.length} image${uploadedImages.length > 1 ? 's' : ''}`, { title: 'Upload Complete' });
      }
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }

  // Handle Unsplash images added
  function handleUnsplashImagesAdded(uploadedImages: Array<{ url: string; attachment_id: string }>) {
    const newImages: ImageObject[] = uploadedImages.map((img, index) => ({
      url: img.url,
      attachment_id: img.attachment_id,
      sort_order: images.length + index,
    }));

    setImages([...images, ...newImages]);
  }

  async function handleVideoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    if (!file.type.startsWith('video/')) {
      notificationStore.error(`${file.name} is not a video file`, { title: 'Upload Error' });
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
      return;
    }
    
    setIsUploadingVideo(true);
    try {
      const attachment = await attachmentsService.upload(file, {
        title: file.name.replace(/\.[^.]+$/, ''),
        tags: [`destination:${city}`, `country:${country}`, 'video'],
      });
      
      // Generate thumbnail from video (first frame) - for now, use video URL as thumbnail
      // In production, you might want to generate an actual thumbnail
      setVideo({
        url: attachment.url,
        attachment_id: attachment.id,
        thumbnail: attachment.url, // Placeholder - could generate actual thumbnail
      });
      
      notificationStore.success('Video uploaded successfully', { title: 'Upload Complete' });
    } catch (error: any) {
      console.error(`Failed to upload ${file.name}:`, error);
      notificationStore.error(`Failed to upload ${file.name}`, { title: 'Upload Error' });
    } finally {
      setIsUploadingVideo(false);
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  }
  
  function removeVideo() {
    setVideo(null);
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  }
  
  // Activity functions
  function resetActivityForm() {
    setName('');
    setDescription('');
    setCategory('');
    setIntensity('');
    setDurationHours('');
    setPrice('');
    setActivityImage(null);
    setEditingActivity(null);
    setIsCreatingActivity(false);
    if (activityFileInputRef.current) {
      activityFileInputRef.current.value = '';
    }
  }
  
  function startCreateActivity() {
    resetActivityForm();
    setIsCreatingActivity(true);
  }
  
  function startEditActivity(activity: DestinationActivity) {
    setEditingActivity(activity);
    setName(activity.name);
    setDescription(activity.description || '');
    setCategory(activity.category || '');
    setIntensity(activity.intensity || '');
    setDurationHours(activity.duration_hours?.toString() || '');
    setPrice(activity.estimated_price_per_person?.toString() || '');
    setActivityImage(activity.images && activity.images.length > 0 ? activity.images[0] : null);
    setIsCreatingActivity(false);
    if (activityFileInputRef.current) {
      activityFileInputRef.current.value = '';
    }
  }
  
  async function handleSaveActivity() {
    if (!name.trim()) {
      notificationStore.error('Activity name is required', { title: 'Validation Error' });
      return;
    }
    
    try {
      setIsSavingActivity(true);
      
      const images: ImageObject[] = activityImage ? [activityImage] : [];
      
      if (editingActivity) {
        // Update existing activity
        if (isProposalContext && researchDestinationId) {
          const input = {
            name: name.trim(),
            description: description.trim() || null,
            category: category || null,
            intensity: intensity || null,
            duration_hours: durationHours ? parseFloat(durationHours) : null,
            estimated_price_per_person: price ? parseFloat(price) : null,
            images: images.length > 0 ? images : undefined,
          };
          await proposalService.updateResearchActivity(researchDestinationId, editingActivity.id, input);
        } else {
          const input: UpdateActivityInput = {
            name: name.trim(),
            description: description.trim() || null,
            category: category || null,
            intensity: intensity || null,
            duration_hours: durationHours ? parseFloat(durationHours) : null,
            estimated_price_per_person: price ? parseFloat(price) : null,
            images: images.length > 0 ? images : undefined,
          };
          await proposalService.updateActivity(editingActivity.id, input);
        }
        notificationStore.success('Activity updated', { title: 'Success' });
      } else {
        // Create new activity
        if (isProposalContext && researchDestinationId) {
          const input = {
            name: name.trim(),
            description: description.trim() || null,
            category: category || null,
            intensity: intensity || null,
            duration_hours: durationHours ? parseFloat(durationHours) : null,
            estimated_price_per_person: price ? parseFloat(price) : null,
            images: images.length > 0 ? images : undefined,
          };
          await proposalService.createResearchActivity(researchDestinationId, input);
        } else {
          const input: CreateActivityInput = {
            city,
            country,
            name: name.trim(),
            description: description.trim() || undefined,
            category: category || undefined,
            intensity: intensity || undefined,
            duration_hours: durationHours ? parseFloat(durationHours) : undefined,
            estimated_price_per_person: price ? parseFloat(price) : undefined,
            images: images.length > 0 ? images : undefined,
          };
          await proposalService.createActivity(input);
        }
        notificationStore.success('Activity created', { title: 'Success' });
      }
      
      resetActivityForm();
      await loadActivities();
      onActivitiesChanged?.();
    } catch (error: any) {
      console.error('Failed to save activity:', error);
      notificationStore.error(error?.message || 'Failed to save activity', { title: 'Save Error' });
    } finally {
      setIsSavingActivity(false);
    }
  }
  
  async function handleActivityImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    if (!file.type.startsWith('image/')) {
      notificationStore.error(`${file.name} is not an image file`, { title: 'Upload Error' });
      if (activityFileInputRef.current) {
        activityFileInputRef.current.value = '';
      }
      return;
    }
    
    setIsUploadingActivity(true);
    try {
      const attachment = await attachmentsService.upload(file, {
        title: file.name.replace(/\.[^.]+$/, ''),
        tags: [`activity:${city}`, `country:${country}`],
      });
      
      setActivityImage({
        url: attachment.url,
        attachment_id: attachment.id,
        sort_order: 0,
      });
      
      notificationStore.success('Image uploaded successfully', { title: 'Upload Complete' });
    } catch (error: any) {
      console.error(`Failed to upload ${file.name}:`, error);
      notificationStore.error(`Failed to upload ${file.name}`, { title: 'Upload Error' });
    } finally {
      setIsUploadingActivity(false);
      if (activityFileInputRef.current) {
        activityFileInputRef.current.value = '';
      }
    }
  }
  
  function removeActivityImage() {
    setActivityImage(null);
    if (activityFileInputRef.current) {
      activityFileInputRef.current.value = '';
    }
  }
  
  async function handleDeleteActivity(activityId: string) {
    try {
      setDeletingActivityId(activityId);

      if (isProposalContext && researchDestinationId) {
        await proposalService.deleteResearchActivity(researchDestinationId, activityId);
      } else {
        await proposalService.deleteActivity(activityId);
      }

      notificationStore.success('Activity deleted', { title: 'Success' });
      await loadActivities();
      onActivitiesChanged?.();

      if (editingActivity?.id === activityId) {
        resetActivityForm();
      }
    } catch (error: any) {
      console.error('Failed to delete activity:', error);
      notificationStore.error(error?.message || 'Failed to delete activity', { title: 'Delete Error' });
    } finally {
      setDeletingActivityId(null);
    }
  }

  /**
   * Restore an archived activity (inverse of soft delete).
   * - Proposal context: flip is_included back to true on the research_destination_activities row.
   * - Global library: flip is_active back to true on the destination_activities row.
   */
  async function handleRestoreActivity(activityId: string) {
    try {
      setRestoringActivityId(activityId);

      if (isProposalContext && researchDestinationId) {
        await proposalService.updateResearchActivity(researchDestinationId, activityId, { is_included: true });
      } else {
        await proposalService.updateActivity(activityId, { is_active: true });
      }

      notificationStore.success('Activity restored', { title: 'Success' });
      await loadActivities();
      onActivitiesChanged?.();
    } catch (error: any) {
      console.error('Failed to restore activity:', error);
      notificationStore.error(error?.message || 'Failed to restore activity', { title: 'Restore Error' });
    } finally {
      setRestoringActivityId(null);
    }
  }

  /**
   * Permanently delete an activity (hard delete).
   * Proposal context already does hard delete via handleDeleteActivity, so this
   * is only used in the global library context.
   */
  async function handleHardDeleteActivity(activityId: string) {
    try {
      setHardDeletingActivityId(activityId);
      await proposalService.deleteActivity(activityId, { hard: true });
      notificationStore.success('Activity permanently deleted', { title: 'Success' });
      await loadActivities();
      onActivitiesChanged?.();

      if (editingActivity?.id === activityId) {
        resetActivityForm();
      }
    } catch (error: any) {
      console.error('Failed to permanently delete activity:', error);
      notificationStore.error(error?.message || 'Failed to permanently delete activity', { title: 'Delete Error' });
    } finally {
      setHardDeletingActivityId(null);
      setHardDeleteActivityConfirm(null);
    }
  }
  
  // ===============================
  // Weather Functions (Proposal Context Only)
  // ===============================
  
  async function loadWeather() {
    if (!weatherDestinationId) return;

    try {
      setIsLoadingWeather(true);
      const weatherData = await proposalService.getResearchWeather(weatherDestinationId);
      setWeather(weatherData);
      // Detect previously saved all-zero annual data (from before the two-phase fix)
      if (weatherData?.annual_climate_data && weatherData.annual_climate_data.length === 12) {
        const allZeros = weatherData.annual_climate_data.every(
          m => m.avg_high === 0 && m.avg_low === 0 && m.rainfall === 0
        );
        setIsAnnualDataPending(allZeros);
      } else {
        setIsAnnualDataPending(false);
      }
    } catch (error) {
      console.error('Failed to load weather:', error);
      // Weather might not exist yet, that's okay
      setWeather(null);
      setIsAnnualDataPending(false);
    } finally {
      setIsLoadingWeather(false);
    }
  }
  
  async function handleResearchWeather() {
    if (!weatherDestinationId || !eventData) return;
    
    try {
      setIsResearchingWeather(true);
      
      // Calculate travel month from event data
      const travelMonth = getTravelMonthForWeather({
        date_type: eventData.date_type || null,
        event_month: eventData.event_month || null,
        start_date: eventData.start_date || null,
        end_date: eventData.end_date || null,
      });
      
      // Research and automatically save weather data
      // The backend API saves the data automatically, so no manual save needed
      const result = await proposalService.researchWeather(
        weatherDestinationId,
        city,
        country,
        travelMonth
      );

      // Update local state with the saved weather data
      setWeather(result.weather);
      setIsAnnualDataPending(result.annual_data_pending ?? false);

      if (result.annual_data_pending) {
        notificationStore.success(
          `Weather data saved for ${travelMonth}. The annual climate chart couldn't be generated — use "Retry Climate Chart" below to try again.`,
          { title: 'Weather Research Partial' }
        );
      } else {
        notificationStore.success(
          `Weather data researched and saved for ${travelMonth}. You can close this modal and return anytime to view or edit the data.`,
          { title: 'Weather Research Complete' }
        );
      }
    } catch (error: any) {
      console.error('Failed to research weather:', error);
      notificationStore.error(error?.message || 'Failed to research weather data', { title: 'Weather Research Error' });
    } finally {
      setIsResearchingWeather(false);
    }
  }
  
  async function handleSaveWeather() {
    if (!weatherDestinationId) return;
    
    try {
      setIsSavingWeather(true);
      
      // Get current form values (use weather state or empty values)
      const currentWeather = weather || {
        day_avg_temperature: null,
        night_avg_temperature: null,
        rainfall: null,
        rainy_days: null,
        humidity: null,
        weather_description: null,
        travel_tip_weather: null,
        travel_tip_packing: null,
        travel_tip_local_events: null,
        annual_climate_data: getAnnualClimateData(),
        travel_month: eventData ? getTravelMonthForWeather({
          date_type: eventData.date_type || null,
          event_month: eventData.event_month || null,
          start_date: eventData.start_date || null,
          end_date: eventData.end_date || null,
        }) : null,
      };
      
      const input: UpsertResearchWeatherInput = {
        day_avg_temperature: currentWeather.day_avg_temperature,
        night_avg_temperature: currentWeather.night_avg_temperature,
        rainfall: currentWeather.rainfall,
        rainy_days: currentWeather.rainy_days,
        humidity: currentWeather.humidity,
        weather_description: currentWeather.weather_description,
        travel_tip_weather: currentWeather.travel_tip_weather,
        travel_tip_packing: currentWeather.travel_tip_packing,
        travel_tip_local_events: currentWeather.travel_tip_local_events,
        annual_climate_data: currentWeather.annual_climate_data,
        travel_month: currentWeather.travel_month,
      };
      
      const updatedWeather = await proposalService.updateResearchWeather(weatherDestinationId, input);
      setWeather(updatedWeather);
      notificationStore.success('Weather data saved', { title: 'Success' });
    } catch (error: any) {
      console.error('Failed to save weather:', error);
      notificationStore.error(error?.message || 'Failed to save weather data', { title: 'Save Error' });
    } finally {
      setIsSavingWeather(false);
    }
  }
  
  // ===============================
  // Event Functions
  // ===============================
  
  async function loadEvents() {
    try {
      setIsLoadingEvents(true);
      const items = await proposalService.listEvents(city, country, true);
      setEvents(items);
    } catch (error) {
      console.error('Failed to load events:', error);
      notificationStore.error('Failed to load events', { title: 'Load Error' });
    } finally {
      setIsLoadingEvents(false);
    }
  }
  
  function resetEventForm() {
    setEventName('');
    setEventDescription('');
    setEventTag('');
    setEventPrice('');
    setEventParticipants('');
    setEventImage(null);
    setEditingEvent(null);
    setIsCreatingEvent(false);
    if (eventFileInputRef.current) {
      eventFileInputRef.current.value = '';
    }
  }
  
  function startCreateEvent() {
    resetEventForm();
    setIsCreatingEvent(true);
  }
  
  function startEditEvent(event: DestinationEvent) {
    setEditingEvent(event);
    setEventName(event.name);
    setEventDescription(event.description || '');
    setEventTag(event.tag || '');
    setEventPrice(event.estimated_price_per_person?.toString() || '');
    setEventParticipants(event.participants || '');
    setEventImage(event.images && event.images.length > 0 ? event.images[0] : null);
    setIsCreatingEvent(false);
    if (eventFileInputRef.current) {
      eventFileInputRef.current.value = '';
    }
  }
  
  async function handleSaveEvent() {
    if (!eventName.trim()) {
      notificationStore.error('Event name is required', { title: 'Validation Error' });
      return;
    }
    
    try {
      setIsSavingEvent(true);
      
      const images: ImageObject[] = eventImage ? [eventImage] : [];
      
      if (editingEvent) {
        const input: UpdateEventInput = {
          name: eventName.trim(),
          description: eventDescription.trim() || null,
          tag: eventTag || null,
          estimated_price_per_person: eventPrice ? parseFloat(eventPrice) : null,
          participants: eventParticipants.trim() || null,
          images: images.length > 0 ? images : undefined,
        };
        
        await proposalService.updateEvent(editingEvent.id, input);
        notificationStore.success('Event updated', { title: 'Success' });
      } else {
        const input: CreateEventInput = {
          city,
          country,
          name: eventName.trim(),
          description: eventDescription.trim() || undefined,
          tag: eventTag || undefined,
          estimated_price_per_person: eventPrice ? parseFloat(eventPrice) : undefined,
          participants: eventParticipants.trim() || undefined,
          images: images.length > 0 ? images : undefined,
        };
        
        await proposalService.createEvent(input);
        notificationStore.success('Event created', { title: 'Success' });
      }
      
      resetEventForm();
      await loadEvents();
      onActivitiesChanged?.(); // Reuse same callback for events
    } catch (error: any) {
      console.error('Failed to save event:', error);
      notificationStore.error(error?.message || 'Failed to save event', { title: 'Save Error' });
    } finally {
      setIsSavingEvent(false);
    }
  }
  
  async function handleDeleteEvent(eventId: string) {
    try {
      setDeletingEventId(eventId);
      await proposalService.deleteEvent(eventId);
      notificationStore.success('Event deleted', { title: 'Success' });
      await loadEvents();
      onActivitiesChanged?.();

      if (editingEvent?.id === eventId) {
        resetEventForm();
      }
    } catch (error: any) {
      console.error('Failed to delete event:', error);
      notificationStore.error(error?.message || 'Failed to delete event', { title: 'Delete Error' });
    } finally {
      setDeletingEventId(null);
    }
  }

  /**
   * Restore an archived event (inverse of soft delete).
   * Events are always global library — flip is_active back to true on destination_events.
   */
  async function handleRestoreEvent(eventId: string) {
    try {
      setRestoringEventId(eventId);
      await proposalService.updateEvent(eventId, { is_active: true });
      notificationStore.success('Event restored', { title: 'Success' });
      await loadEvents();
      onActivitiesChanged?.();
    } catch (error: any) {
      console.error('Failed to restore event:', error);
      notificationStore.error(error?.message || 'Failed to restore event', { title: 'Restore Error' });
    } finally {
      setRestoringEventId(null);
    }
  }

  /**
   * Permanently delete an event (hard delete from destination_events).
   */
  async function handleHardDeleteEvent(eventId: string) {
    try {
      setHardDeletingEventId(eventId);
      await proposalService.deleteEvent(eventId, { hard: true });
      notificationStore.success('Event permanently deleted', { title: 'Success' });
      await loadEvents();
      onActivitiesChanged?.();

      if (editingEvent?.id === eventId) {
        resetEventForm();
      }
    } catch (error: any) {
      console.error('Failed to permanently delete event:', error);
      notificationStore.error(error?.message || 'Failed to permanently delete event', { title: 'Delete Error' });
    } finally {
      setHardDeletingEventId(null);
      setHardDeleteEventConfirm(null);
    }
  }
  
  async function handleEventsDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const oldIndex = events.findIndex((e) => e.id === active.id);
    const newIndex = events.findIndex((e) => e.id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    const reorderedEvents = arrayMove(events, oldIndex, newIndex);
    setEvents(reorderedEvents);
    
    try {
      setIsReorderingEvents(true);
      
      const updatePromises = reorderedEvents.map((e, index) =>
        proposalService.updateEvent(e.id, { sort_order: index })
      );
      
      await Promise.all(updatePromises);
      notificationStore.success('Event order updated', { title: 'Success' });
    } catch (error) {
      console.error('Failed to update event order:', error);
      notificationStore.error('Failed to update event order', { title: 'Error' });
      await loadEvents();
    } finally {
      setIsReorderingEvents(false);
    }
  }
  
  async function handleEventImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      notificationStore.error(`${file.name} is not an image file`, { title: 'Upload Error' });
      if (eventFileInputRef.current) {
        eventFileInputRef.current.value = '';
      }
      return;
    }
    
    setIsUploadingEventImage(true);
    try {
      const attachment = await attachmentsService.upload(file, {
        title: file.name.replace(/\.[^.]+$/, ''),
        tags: ['destination_events'],
      });
      
      setEventImage({
        url: attachment.url,
        attachment_id: attachment.id,
        sort_order: 0,
      });
      
      notificationStore.success('Image uploaded successfully', { title: 'Upload Complete' });
    } catch (error: any) {
      console.error(`Failed to upload ${file.name}:`, error);
      notificationStore.error(`Failed to upload ${file.name}`, { title: 'Upload Error' });
    } finally {
      setIsUploadingEventImage(false);
      if (eventFileInputRef.current) {
        eventFileInputRef.current.value = '';
      }
    }
  }
  
  function removeEventImage() {
    setEventImage(null);
    if (eventFileInputRef.current) {
      eventFileInputRef.current.value = '';
    }
  }
  
  function getCategoryInfo(cat: ActivityCategory | null) {
    return CATEGORY_OPTIONS.find(c => c.value === cat);
  }
  
  function getIntensityInfo(int: ActivityIntensity | null) {
    return INTENSITY_OPTIONS.find(i => i.value === int);
  }
  
  function getTagInfo(tag: EventTag | null) {
    return TAG_OPTIONS.find(t => t.value === tag);
  }
  
  const isEditingActivity = isCreatingActivity || editingActivity !== null;
  const activeActivities = activities.filter(a => a.is_active);
  const archivedActivitiesCount = activities.length - activeActivities.length;
  const visibleActivities = showArchivedActivities ? activities : activeActivities;
  const isEditingEvent = isCreatingEvent || editingEvent !== null;
  const activeEvents = events.filter(e => e.is_active);
  const archivedEventsCount = events.length - activeEvents.length;
  const visibleEvents = showArchivedEvents ? events : activeEvents;
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit ${city}`}
      description={`Customize destination content and activities for ${city}, ${country}`}
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSaving || isSavingActivity || isResearchingContent}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            isLoading={isSaving}
            disabled={isResearchingContent}
            leftIcon={<Check className="w-4 h-4" />}
          >
            Save Changes
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Header Info */}
        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
          <MapPin className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{city}</p>
                <p className="text-sm text-muted-foreground">{country}</p>
              </div>
              {existingContent && (
                <Badge variant="success" className="text-xs flex-shrink-0 sm:hidden">Content Exists</Badge>
              )}
            </div>
            {existingContent && (
              <a
                href="/organization/destinations"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700 hover:underline mt-2 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                <span>Edit global destination per org</span>
              </a>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {existingContent && (
              <Badge variant="success" className="text-xs hidden sm:inline-flex">Content Exists</Badge>
            )}
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex w-full justify-start overflow-x-auto hide-scrollbar">
            <TabsTrigger value="general" className="flex-shrink-0">
              <Info className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">General</span>
            </TabsTrigger>
            <TabsTrigger value="images" className="flex-shrink-0">
              <FileImage className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Media</span>
            </TabsTrigger>
            {isProposalContext && (
              <TabsTrigger value="weather" className="flex-shrink-0">
                <Cloud className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Weather</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="activities" className="flex-shrink-0">
              <Activity className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Activities</span>
              <span className="ml-1 text-xs">({activeActivities.length})</span>
            </TabsTrigger>
            <TabsTrigger value="events" className="flex-shrink-0">
              <Calendar className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Events</span>
              <span className="ml-1 text-xs">({events.filter(e => e.is_active).length})</span>
            </TabsTrigger>
          </TabsList>
          
          {/* General Info Tab */}
          <TabsContent value="general" className="mt-4 space-y-4 max-h-[50vh] overflow-y-auto pr-2">
            {/* AI Generate Button */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div>
                <p className="font-medium text-blue-900">AI Content Generation</p>
                <p className="text-sm text-blue-700">
                  Generate description, key pros, and considerations for {city}, {country}
                </p>
              </div>
              <Button
                variant="primary"
                className="sm:flex-shrink-0"
                onClick={handlePopulateContent}
                isLoading={isResearchingContent}
                leftIcon={<Sparkles className="w-4 h-4" />}
                disabled={isResearchingContent}
              >
                Generate with AI
              </Button>
            </div>

            {/* Short Description */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <Textarea
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                placeholder="Describe the destination - its unique qualities, why it's great for events, local culture, etc."
                rows={4}
                maxLength={1000}
              />
              <p className="text-xs text-muted-foreground">
                {shortDescription.length}/1000 characters
              </p>
            </div>
            
            {/* Key Pros */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Key Pros ({keyPros.length}/10)
              </label>
              <div className="flex gap-2">
                <Input
                  value={newPro}
                  onChange={(e) => setNewPro(e.target.value)}
                  placeholder="Add a selling point..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPro())}
                />
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={addPro}
                  disabled={!newPro.trim() || keyPros.length >= 10}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {keyPros.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {keyPros.map((pro, index) => (
                    <Badge 
                      key={index} 
                      variant="success"
                      className="flex items-center gap-1"
                    >
                      <Check className="w-3 h-3" />
                      {pro}
                      <button 
                        onClick={() => removePro(index)}
                        className="ml-1 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            {/* Considerations */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Considerations ({considerations.length}/10)
              </label>
              <div className="flex gap-2">
                <Input
                  value={newConsideration}
                  onChange={(e) => setNewConsideration(e.target.value)}
                  placeholder="Add a consideration or potential drawback..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addConsideration())}
                />
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={addConsideration}
                  disabled={!newConsideration.trim() || considerations.length >= 10}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {considerations.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {considerations.map((item, index) => (
                    <Badge 
                      key={index} 
                      variant="warning"
                      className="flex items-center gap-1"
                    >
                      <AlertCircle className="w-3 h-3" />
                      {item}
                      <button 
                        onClick={() => removeConsideration(index)}
                        className="ml-1 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
          
          {/* Media Tab */}
          <TabsContent value="images" className="mt-4 space-y-6 max-h-[50vh] overflow-y-auto pr-2">
            {/* Images Section */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Images ({images.length}/5)
              </label>
              
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isUploading || images.length >= 5}
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading || images.length >= 5}
                  leftIcon={isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                >
                  {isUploading ? 'Uploading...' : images.length >= 5 ? 'Limit Reached (5/5)' : 'Upload Images'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsUnsplashSearchOpen(true)}
                  disabled={!existingContent?.id || images.length >= 5}
                  leftIcon={<Search className="w-4 h-4" />}
                >
                  Search Unsplash
                </Button>
              </div>
              
              {images.length > 0 && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={images.map((img) => getImageId(img))}
                    strategy={rectSortingStrategy}
                  >
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 mt-2">
                      {images.map((img, index) => (
                        <SortableImageItem
                          key={getImageId(img)}
                          id={getImageId(img)}
                          image={img}
                          city={city}
                          index={index}
                          onRemove={() => removeImage(index)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
              
              <p className="text-xs text-muted-foreground">
                Upload images from your computer or search Unsplash for destination photos. Maximum 5 images per destination. Images are stored in your attachments library and associated with this destination.
              </p>
              {!existingContent?.id && (
                <p className="text-xs text-amber-600 mt-1">
                  Note: Save the destination first to enable Unsplash search.
                </p>
              )}
            </div>
            
            {/* Video Section */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Video (Optional)
              </label>
              
              {video ? (
                <div className="relative border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 rounded bg-gray-200 flex items-center justify-center">
                        <ImageIcon className="w-6 h-6 text-gray-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Video uploaded</p>
                        <p className="text-xs text-muted-foreground">Click remove to change video</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={removeVideo}
                      className="text-error-600 hover:text-error-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="hidden"
                    disabled={isUploadingVideo}
                  />
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={() => videoInputRef.current?.click()}
                    disabled={isUploadingVideo}
                    leftIcon={isUploadingVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  >
                    {isUploadingVideo ? 'Uploading...' : 'Upload Video'}
                  </Button>
                </div>
              )}
              
              <p className="text-xs text-muted-foreground">
                Upload a video file (MP4, WebM, etc.). Maximum 1 video per destination. Video is stored in your attachments library.
              </p>
            </div>
            
            {/* Display Option */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Display Option
              </label>
              <select
                value={mediaLayout || ''}
                onChange={(e) => setMediaLayout(e.target.value as 'gallery' | 'grid' | 'video' | null || null)}
                className="w-full h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Auto (default)</option>
                <option value="gallery">Gallery (carousel)</option>
                <option value="grid">Grid (first 3 images)</option>
                {video && <option value="video">Video (video hero)</option>}
              </select>
              <p className="text-xs text-muted-foreground">
                Choose how media is displayed on the destination page. Gallery shows all images in a carousel, Grid shows first 3 images in a masonry layout, Video shows the video as hero (only available if video is uploaded).
              </p>
            </div>
          </TabsContent>
          
          {/* Weather Tab (Proposal Context Only) */}
          {isProposalContext && (
            <TabsContent value="weather" className="mt-4 space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {/* AI Research Weather Button */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <p className="font-medium text-blue-900">AI Weather Research</p>
                  <p className="text-sm text-blue-700">
                    {eventData
                      ? `Generate weather data for ${city}, ${country} based on event dates`
                      : 'Event dates required for weather research'}
                  </p>
                </div>
                <Button
                  variant="primary"
                  className="sm:flex-shrink-0"
                  onClick={handleResearchWeather}
                  isLoading={isResearchingWeather}
                  disabled={!eventData || isResearchingWeather}
                  leftIcon={<Sparkles className="w-4 h-4" />}
                >
                  {isResearchingWeather ? 'Researching...' : 'Generate Weather'}
                </Button>
              </div>

              {/* Annual climate data pending warning */}
              {isAnnualDataPending && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-amber-50 rounded-lg border border-amber-300">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-900">Annual Climate Chart Incomplete</p>
                      <p className="text-sm text-amber-700">
                        Travel month data was saved successfully, but the 12-month climate chart data could not be generated. Click retry to attempt again.
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    className="sm:flex-shrink-0 border-amber-400 text-amber-800 hover:bg-amber-100"
                    onClick={handleResearchWeather}
                    isLoading={isResearchingWeather}
                    disabled={!eventData || isResearchingWeather}
                    leftIcon={<RotateCcw className="w-4 h-4" />}
                  >
                    {isResearchingWeather ? 'Retrying...' : 'Retry Climate Chart'}
                  </Button>
                </div>
              )}

              {isLoadingWeather ? (
                <div className="py-8 text-center text-muted-foreground">
                  Loading weather data...
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Always show the form - use weather data or empty values */}
                  {(() => {
                    const weatherData = getWeatherData();
                    const annualData = getAnnualClimateData();
                    
                    return (
                      <>
                        {/* Travel Month */}
                        {eventData && (
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-600" />
                              <span className="text-sm font-medium">Travel Month:</span>
                              <span className="text-sm text-gray-700">
                                {getTravelMonthForWeather({
                                  date_type: eventData.date_type || null,
                                  event_month: eventData.event_month || null,
                                  start_date: eventData.start_date || null,
                                  end_date: eventData.end_date || null,
                                })}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Temperature */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <label className="block text-sm font-medium text-gray-700">
                              Day Avg Temperature (°C)
                            </label>
                            <Input
                              type="number"
                              value={weatherData?.day_avg_temperature?.toString() || ''}
                              onChange={(e) => setWeather({
                                ...(weatherData || {
                                  id: '',
                                  research_destination_id: weatherDestinationId || '',
                                  day_avg_temperature: null,
                                  night_avg_temperature: null,
                                  rainfall: null,
                                  rainy_days: null,
                                  humidity: null,
                                  weather_description: null,
                                  travel_tip_weather: null,
                                  travel_tip_packing: null,
                                  travel_tip_local_events: null,
                                  annual_climate_data: annualData,
                                  travel_month: null,
                                  researched_at: null,
                                  llm_log_id: null,
                                  created_at: new Date().toISOString(),
                                  updated_at: new Date().toISOString(),
                                }),
                                day_avg_temperature: e.target.value ? parseFloat(e.target.value) : null
                              })}
                              placeholder="e.g., 25"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="block text-sm font-medium text-gray-700">
                              Night Avg Temperature (°C)
                            </label>
                            <Input
                              type="number"
                              value={weatherData?.night_avg_temperature?.toString() || ''}
                              onChange={(e) => setWeather({
                                ...(weatherData || {
                                  id: '',
                                  research_destination_id: weatherDestinationId || '',
                                  day_avg_temperature: null,
                                  night_avg_temperature: null,
                                  rainfall: null,
                                  rainy_days: null,
                                  humidity: null,
                                  weather_description: null,
                                  travel_tip_weather: null,
                                  travel_tip_packing: null,
                                  travel_tip_local_events: null,
                                  annual_climate_data: annualData,
                                  travel_month: null,
                                  researched_at: null,
                                  llm_log_id: null,
                                  created_at: new Date().toISOString(),
                                  updated_at: new Date().toISOString(),
                                }),
                                night_avg_temperature: e.target.value ? parseFloat(e.target.value) : null
                              })}
                              placeholder="e.g., 15"
                            />
                          </div>
                        </div>

                        {/* Rainfall & Humidity */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-1">
                            <label className="block text-sm font-medium text-gray-700">
                              Rainfall (mm)
                            </label>
                            <Input
                              type="number"
                              value={weatherData?.rainfall?.toString() || ''}
                              onChange={(e) => setWeather({
                                ...(weatherData || {
                                  id: '',
                                  research_destination_id: weatherDestinationId || '',
                                  day_avg_temperature: null,
                                  night_avg_temperature: null,
                                  rainfall: null,
                                  rainy_days: null,
                                  humidity: null,
                                  weather_description: null,
                                  travel_tip_weather: null,
                                  travel_tip_packing: null,
                                  travel_tip_local_events: null,
                                  annual_climate_data: annualData,
                                  travel_month: null,
                                  researched_at: null,
                                  llm_log_id: null,
                                  created_at: new Date().toISOString(),
                                  updated_at: new Date().toISOString(),
                                }),
                                rainfall: e.target.value ? parseFloat(e.target.value) : null
                              })}
                              placeholder="e.g., 50"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="block text-sm font-medium text-gray-700">
                              Rainy Days
                            </label>
                            <Input
                              type="number"
                              value={weatherData?.rainy_days?.toString() || ''}
                              onChange={(e) => setWeather({
                                ...(weatherData || {
                                  id: '',
                                  research_destination_id: weatherDestinationId || '',
                                  day_avg_temperature: null,
                                  night_avg_temperature: null,
                                  rainfall: null,
                                  rainy_days: null,
                                  humidity: null,
                                  weather_description: null,
                                  travel_tip_weather: null,
                                  travel_tip_packing: null,
                                  travel_tip_local_events: null,
                                  annual_climate_data: annualData,
                                  travel_month: null,
                                  researched_at: null,
                                  llm_log_id: null,
                                  created_at: new Date().toISOString(),
                                  updated_at: new Date().toISOString(),
                                }),
                                rainy_days: e.target.value ? parseInt(e.target.value) : null
                              })}
                              placeholder="e.g., 5"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="block text-sm font-medium text-gray-700">
                              Humidity (%)
                            </label>
                            <Input
                              type="number"
                              value={weatherData?.humidity?.toString() || ''}
                              onChange={(e) => setWeather({
                                ...(weatherData || {
                                  id: '',
                                  research_destination_id: weatherDestinationId || '',
                                  day_avg_temperature: null,
                                  night_avg_temperature: null,
                                  rainfall: null,
                                  rainy_days: null,
                                  humidity: null,
                                  weather_description: null,
                                  travel_tip_weather: null,
                                  travel_tip_packing: null,
                                  travel_tip_local_events: null,
                                  annual_climate_data: annualData,
                                  travel_month: null,
                                  researched_at: null,
                                  llm_log_id: null,
                                  created_at: new Date().toISOString(),
                                  updated_at: new Date().toISOString(),
                                }),
                                humidity: e.target.value ? parseFloat(e.target.value) : null
                              })}
                              placeholder="e.g., 65"
                            />
                          </div>
                        </div>

                        {/* Weather Description */}
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-gray-700">
                            Weather Description
                          </label>
                          <Textarea
                            value={weatherData?.weather_description || ''}
                            onChange={(e) => setWeather({
                              ...(weatherData || {
                                id: '',
                                research_destination_id: weatherDestinationId || '',
                                day_avg_temperature: null,
                                night_avg_temperature: null,
                                rainfall: null,
                                rainy_days: null,
                                humidity: null,
                                weather_description: null,
                                travel_tip_weather: null,
                                travel_tip_packing: null,
                                travel_tip_local_events: null,
                                annual_climate_data: annualData,
                                travel_month: null,
                                researched_at: null,
                                llm_log_id: null,
                                created_at: new Date().toISOString(),
                                updated_at: new Date().toISOString(),
                              }),
                              weather_description: e.target.value || null
                            })}
                            placeholder="Describe the typical weather conditions..."
                            rows={3}
                          />
                        </div>

                        {/* Annual Climate Data Table */}
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Annual Climate Overview
                          </label>
                          <div className="border rounded-lg overflow-hidden">
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Month</th>
                                    <th className="px-3 py-2 text-right font-medium text-gray-700 border-b">Avg High (°C)</th>
                                    <th className="px-3 py-2 text-right font-medium text-gray-700 border-b">Avg Low (°C)</th>
                                    <th className="px-3 py-2 text-right font-medium text-gray-700 border-b">Rainfall (mm)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {annualData.map((monthData, index) => (
                                    <tr key={monthData.month} className="border-b hover:bg-gray-50">
                                      <td className="px-3 py-2 font-medium text-gray-700">{monthData.month}</td>
                                      <td className="px-3 py-2">
                                        <Input
                                          type="number"
                                          value={monthData.avg_high === 0 ? '' : monthData.avg_high.toString()}
                                          onChange={(e) => updateAnnualClimateData(index, 'avg_high', e.target.value ? parseFloat(e.target.value) : 0)}
                                          className="w-full text-right"
                                          placeholder="0"
                                        />
                                      </td>
                                      <td className="px-3 py-2">
                                        <Input
                                          type="number"
                                          value={monthData.avg_low === 0 ? '' : monthData.avg_low.toString()}
                                          onChange={(e) => updateAnnualClimateData(index, 'avg_low', e.target.value ? parseFloat(e.target.value) : 0)}
                                          className="w-full text-right"
                                          placeholder="0"
                                        />
                                      </td>
                                      <td className="px-3 py-2">
                                        <Input
                                          type="number"
                                          value={monthData.rainfall === 0 ? '' : monthData.rainfall.toString()}
                                          onChange={(e) => updateAnnualClimateData(index, 'rainfall', e.target.value ? parseFloat(e.target.value) : 0)}
                                          className="w-full text-right"
                                          placeholder="0"
                                        />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Edit monthly climate data. This data is used in the climate report modal on the proposal page.
                          </p>
                        </div>

                        {/* Travel Tips */}
                        <div className="space-y-3">
                          <label className="block text-sm font-medium text-gray-700">
                            Travel Tips
                          </label>
                          <div className="space-y-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Weather Tips</label>
                              <Textarea
                                value={weatherData?.travel_tip_weather || ''}
                                onChange={(e) => setWeather({
                                  ...(weatherData || {
                                    id: '',
                                    research_destination_id: weatherDestinationId || '',
                                    day_avg_temperature: null,
                                    night_avg_temperature: null,
                                    rainfall: null,
                                    rainy_days: null,
                                    humidity: null,
                                    weather_description: null,
                                    travel_tip_weather: null,
                                    travel_tip_packing: null,
                                    travel_tip_local_events: null,
                                    annual_climate_data: annualData,
                                    travel_month: null,
                                    researched_at: null,
                                    llm_log_id: null,
                                    created_at: new Date().toISOString(),
                                    updated_at: new Date().toISOString(),
                                  }),
                                  travel_tip_weather: e.target.value || null
                                })}
                                placeholder="Tips about weather conditions..."
                                rows={2}
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Packing Tips</label>
                              <Textarea
                                value={weatherData?.travel_tip_packing || ''}
                                onChange={(e) => setWeather({
                                  ...(weatherData || {
                                    id: '',
                                    research_destination_id: weatherDestinationId || '',
                                    day_avg_temperature: null,
                                    night_avg_temperature: null,
                                    rainfall: null,
                                    rainy_days: null,
                                    humidity: null,
                                    weather_description: null,
                                    travel_tip_weather: null,
                                    travel_tip_packing: null,
                                    travel_tip_local_events: null,
                                    annual_climate_data: annualData,
                                    travel_month: null,
                                    researched_at: null,
                                    llm_log_id: null,
                                    created_at: new Date().toISOString(),
                                    updated_at: new Date().toISOString(),
                                  }),
                                  travel_tip_packing: e.target.value || null
                                })}
                                placeholder="What to pack for this weather..."
                                rows={2}
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Local Events</label>
                              <Textarea
                                value={weatherData?.travel_tip_local_events || ''}
                                onChange={(e) => setWeather({
                                  ...(weatherData || {
                                    id: '',
                                    research_destination_id: weatherDestinationId || '',
                                    day_avg_temperature: null,
                                    night_avg_temperature: null,
                                    rainfall: null,
                                    rainy_days: null,
                                    humidity: null,
                                    weather_description: null,
                                    travel_tip_weather: null,
                                    travel_tip_packing: null,
                                    travel_tip_local_events: null,
                                    annual_climate_data: annualData,
                                    travel_month: null,
                                    researched_at: null,
                                    llm_log_id: null,
                                    created_at: new Date().toISOString(),
                                    updated_at: new Date().toISOString(),
                                  }),
                                  travel_tip_local_events: e.target.value || null
                                })}
                                placeholder="Local events or festivals during this time..."
                                rows={2}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end pt-2">
                          <Button
                            variant="primary"
                            onClick={handleSaveWeather}
                            isLoading={isSavingWeather}
                            leftIcon={<Check className="w-4 h-4" />}
                          >
                            Save Weather Data
                          </Button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </TabsContent>
          )}
          
          {/* Activities Tab */}
          <TabsContent value="activities" className="mt-4 space-y-4 max-h-[50vh] overflow-y-auto pr-2">
            {onActivitySourceChange && <ProposalActivitySourcePicker city={city} value={researchDestinationId} options={activitySourceOptions} onChange={onActivitySourceChange}/>}
            <fieldset disabled={isProposalContext && !researchDestinationId} className="space-y-4 min-w-0">

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-accent-600" />
                <span className="font-medium">{activeActivities.length} Activities</span>
                {isProposalContext && (
                  <Badge variant="secondary" className="text-xs">
                    Proposal-specific
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isProposalContext && (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowDestinationActivities(!showDestinationActivities)}
                      leftIcon={<Search className="w-4 h-4" />}
                    >
                      {showDestinationActivities ? 'Hide' : 'View'} Destination
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowActivityLibraryBrowser(true)}
                      leftIcon={<Database className="w-4 h-4" />}
                    >
                      View Global
                    </Button>
                  </>
                )}
                {!isEditingActivity && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={startCreateActivity}
                    leftIcon={<Plus className="w-4 h-4" />}
                  >
                    Add Activity
                  </Button>
                )}
              </div>
            </div>

            {/* Destination Activities Section (only in proposal context) */}
            {isProposalContext && showDestinationActivities && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardContent noPadding className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-sm">Destination Activities</span>
                      <Badge variant="secondary" className="text-xs">
                        {destinationActivities.length} available
                      </Badge>
                    </div>
                  </div>

                  {isLoadingDestinationActivities ? (
                    <div className="py-4 text-center text-sm text-muted-foreground">
                      Loading destination activities...
                    </div>
                  ) : destinationActivities.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No saved activities for {city}, {country}
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {destinationActivities.map((destinationActivity) => {
                        const alreadyCopied = activities.some(a =>
                          a.name === destinationActivity.name &&
                          a.description === destinationActivity.description
                        );
                        const isCopying = copyingActivityIds.has(destinationActivity.id);

                        return (
                          <div
                            key={destinationActivity.id}
                            className="flex items-start gap-2 p-2 bg-white rounded border border-gray-200"
                          >
                            {destinationActivity.images && destinationActivity.images.length > 0 && (
                              <div className="w-10 h-10 rounded overflow-hidden border border-gray-200 flex-shrink-0">
                                <img
                                  src={destinationActivity.images[0].url}
                                  alt={destinationActivity.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{destinationActivity.name}</p>
                              {destinationActivity.description && (
                                <p className="text-xs text-muted-foreground line-clamp-1">
                                  {destinationActivity.description}
                                </p>
                              )}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCopyDestinationActivity(destinationActivity.id)}
                              disabled={alreadyCopied || isCopying}
                              isLoading={isCopying}
                              className="flex-shrink-0"
                            >
                              {alreadyCopied ? 'Copied' : 'Copy'}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Edit/Create Activity Form */}
            {isEditingActivity && (
              <Card className="border-primary-200 bg-primary-50/50">
                <CardContent noPadding className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">
                      {editingActivity ? 'Edit Activity' : 'New Activity'}
                    </h3>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={resetActivityForm}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Activity Name *
                    </label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g., Wine Tasting Tour"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Describe the activity..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Category
                      </label>
                      <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value as ActivityCategory)}
                        className="w-full h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Select category</option>
                        {CATEGORY_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.emoji} {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Intensity
                      </label>
                      <select
                        value={intensity}
                        onChange={(e) => setIntensity(e.target.value as ActivityIntensity)}
                        className="w-full h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Select intensity</option>
                        {INTENSITY_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Duration (hours)
                      </label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        max="24"
                        value={durationHours}
                        onChange={(e) => setDurationHours(e.target.value)}
                        placeholder="e.g., 2.5"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Price per Person ($)
                      </label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        placeholder="e.g., 150"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Activity Image (Optional)
                    </label>
                    
                    {activityImage ? (
                      <div className="flex items-start gap-4">
                        <div className="relative flex-shrink-0">
                          <div className="relative w-32 h-24 rounded-lg overflow-hidden border border-gray-200" style={{ aspectRatio: '4/3' }}>
                            <img 
                              src={activityImage.url} 
                              alt="Activity preview" 
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={removeActivityImage}
                              className="absolute top-1 right-1 p-1 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                              title="Remove image"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-muted-foreground">
                            Click remove to change image (maximum 1 image per activity)
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                        <input
                          ref={activityFileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleActivityImageUpload}
                          className="hidden"
                          disabled={isUploadingActivity}
                        />
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                            {isUploadingActivity ? (
                              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                            ) : (
                              <ImageIcon className="w-6 h-6 text-gray-400" />
                            )}
                          </div>
                          <p className="text-sm font-medium text-gray-700 mb-1">
                            {isUploadingActivity ? 'Uploading image...' : 'Upload Activity Image'}
                          </p>
                          <p className="text-xs text-muted-foreground mb-3">
                            Maximum 1 image per activity. JPG, PNG, or WebP.
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => activityFileInputRef.current?.click()}
                            disabled={isUploadingActivity}
                            leftIcon={isUploadingActivity ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                          >
                            {isUploadingActivity ? 'Uploading...' : 'Choose Image'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-end gap-2 pt-2">
                    <Button 
                      variant="ghost" 
                      onClick={resetActivityForm}
                      disabled={isSavingActivity}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="primary" 
                      onClick={handleSaveActivity}
                      isLoading={isSavingActivity}
                      leftIcon={<Check className="w-4 h-4" />}
                    >
                      {editingActivity ? 'Save Changes' : 'Create Activity'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Activities List */}
            {isLoadingActivities ? (
              <div className="py-8 text-center text-muted-foreground">
                Loading activities...
              </div>
            ) : activities.length === 0 ? (
              <div className="py-8 text-center">
                <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-muted-foreground">No activities yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Add curated experiences for this destination
                </p>
              </div>
            ) : visibleActivities.length === 0 ? (
              <div className="py-6 text-center">
                <p className="text-muted-foreground text-sm">
                  No active activities. {archivedActivitiesCount > 0 && 'All items are archived — toggle below to view them.'}
                </p>
                {archivedActivitiesCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    onClick={() => setShowArchivedActivities(true)}
                  >
                    Show {archivedActivitiesCount} archived
                  </Button>
                )}
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleActivitiesDragEnd}
              >
                <SortableContext
                  items={visibleActivities.map((activity) => activity.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {visibleActivities.map((activity) => {
                      const catInfo = getCategoryInfo(activity.category);
                      const intInfo = getIntensityInfo(activity.intensity);
                      const isDeleting = deletingActivityId === activity.id;
                      const isRestoring = restoringActivityId === activity.id;
                      const isHardDeleting = hardDeletingActivityId === activity.id;

                      return (
                        <SortableActivityItem
                          key={activity.id}
                          id={activity.id}
                          activity={activity}
                          catInfo={catInfo}
                          intInfo={intInfo}
                          isDeleting={isDeleting}
                          isEditing={editingActivity?.id === activity.id}
                          onEdit={() => startEditActivity(activity)}
                          onDelete={() => handleDeleteActivity(activity.id)}
                          onRestore={() => handleRestoreActivity(activity.id)}
                          isRestoring={isRestoring}
                          onHardDelete={
                            isProposalContext ? undefined : () => setHardDeleteActivityConfirm(activity)
                          }
                          isHardDeleting={isHardDeleting}
                          isReordering={isReorderingActivities}
                          isProposalContext={isProposalContext}
                          onPromoteToDestination={
                            isProposalContext && !activityGlobalIdMap.get(activity.id)
                              ? () => handlePromoteToDestination(activity.id)
                              : undefined
                          }
                          isPromoting={promotingActivityId === activity.id}
                          hasDestinationLink={!!activityGlobalIdMap.get(activity.id)}
                          onSaveToLibrary={
                            isProposalContext
                              ? () => handleSaveToLibrary(activity.id)
                              : undefined
                          }
                          isSavingToLibrary={savingToLibraryId === activity.id}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            {/* Show/Hide archived toggle for activities */}
            {archivedActivitiesCount > 0 && visibleActivities.length > 0 && (
              <div className="pt-2 flex justify-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowArchivedActivities(!showArchivedActivities)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {showArchivedActivities
                    ? `Hide archived (${archivedActivitiesCount})`
                    : `Show archived (${archivedActivitiesCount})`}
                </Button>
              </div>
            )}
            </fieldset>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" className="mt-4 space-y-4 max-h-[50vh] overflow-y-auto pr-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-accent-600" />
                <span className="font-medium">{activeEvents.length} Events</span>
              </div>
              {!isEditingEvent && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEventLibraryOpen(true)}
                    leftIcon={<Database className="w-4 h-4" />}
                  >
                    From Library
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={startCreateEvent}
                    leftIcon={<Plus className="w-4 h-4" />}
                  >
                    Add Event
                  </Button>
                </div>
              )}
            </div>
            
            {/* Edit/Create Event Form */}
            {isEditingEvent && (
              <Card className="border-primary-200 bg-primary-50/50">
                <CardContent noPadding className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">
                      {editingEvent ? 'Edit Event' : 'New Event'}
                    </h3>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={resetEventForm}
                      disabled={isSavingEvent}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Name */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Event Name *
                    </label>
                    <Input
                      value={eventName}
                      onChange={(e) => setEventName(e.target.value)}
                      placeholder="e.g., Welcome Cocktail Reception"
                      maxLength={255}
                    />
                  </div>
                  
                  {/* Description */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <Textarea
                      value={eventDescription}
                      onChange={(e) => setEventDescription(e.target.value)}
                      placeholder="Describe the event..."
                      rows={2}
                    />
                  </div>
                  
                  {/* Tag and Price Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Tag
                      </label>
                      <select
                        value={eventTag}
                        onChange={(e) => setEventTag(e.target.value as EventTag)}
                        className="w-full h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Select tag</option>
                        {TAG_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Price per Person ($)
                      </label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={eventPrice}
                        onChange={(e) => setEventPrice(e.target.value)}
                        placeholder="e.g., 150"
                      />
                    </div>
                  </div>
                  
                  {/* Participants */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Number of Participants
                    </label>
                    <Input
                      value={eventParticipants}
                      onChange={(e) => setEventParticipants(e.target.value)}
                      placeholder="e.g., everyone, all, 50, 25-30"
                      maxLength={100}
                    />
                    <p className="text-xs text-muted-foreground">
                      Freeform text (e.g., "everyone", "all", "50", "25-30")
                    </p>
                  </div>
                  
                  {/* Image Upload */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Event Image (Optional)
                    </label>
                    
                    {eventImage ? (
                      <div className="flex items-start gap-4">
                        <div className="relative flex-shrink-0">
                          <div className="relative w-32 h-24 rounded-lg overflow-hidden border border-gray-200" style={{ aspectRatio: '4/3' }}>
                            <img 
                              src={eventImage.url} 
                              alt="Event preview" 
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={removeEventImage}
                              className="absolute top-1 right-1 p-1 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                              title="Remove image"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-muted-foreground">
                            Click remove to change image (maximum 1 image per event)
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                        <input
                          ref={eventFileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleEventImageUpload}
                          className="hidden"
                          disabled={isUploadingEventImage}
                        />
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                            {isUploadingEventImage ? (
                              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                            ) : (
                              <ImageIcon className="w-6 h-6 text-gray-400" />
                            )}
                          </div>
                          <p className="text-sm font-medium text-gray-700 mb-1">
                            {isUploadingEventImage ? 'Uploading image...' : 'Upload Event Image'}
                          </p>
                          <p className="text-xs text-muted-foreground mb-3">
                            Maximum 1 image per event. JPG, PNG, or WebP.
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => eventFileInputRef.current?.click()}
                            disabled={isUploadingEventImage}
                            leftIcon={isUploadingEventImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                          >
                            {isUploadingEventImage ? 'Uploading...' : 'Choose Image'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Form Actions */}
                  <div className="flex items-center justify-end gap-2 pt-2">
                    <Button 
                      variant="ghost" 
                      onClick={resetEventForm}
                      disabled={isSavingEvent}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="primary" 
                      onClick={handleSaveEvent}
                      isLoading={isSavingEvent}
                      leftIcon={<Check className="w-4 h-4" />}
                    >
                      {editingEvent ? 'Save Changes' : 'Create Event'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Events List */}
            {isLoadingEvents ? (
              <div className="py-8 text-center text-muted-foreground">
                Loading events...
              </div>
            ) : events.length === 0 ? (
              <div className="py-8 text-center">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-muted-foreground">No events yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Add events for this destination
                </p>
              </div>
            ) : visibleEvents.length === 0 ? (
              <div className="py-6 text-center">
                <p className="text-muted-foreground text-sm">
                  No active events. {archivedEventsCount > 0 && 'All items are archived — toggle below to view them.'}
                </p>
                {archivedEventsCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    onClick={() => setShowArchivedEvents(true)}
                  >
                    Show {archivedEventsCount} archived
                  </Button>
                )}
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleEventsDragEnd}
              >
                <SortableContext
                  items={visibleEvents.map((e) => e.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {visibleEvents.map((event) => {
                      const tagInfo = getTagInfo(event.tag);
                      const isDeleting = deletingEventId === event.id;
                      const isRestoring = restoringEventId === event.id;
                      const isHardDeleting = hardDeletingEventId === event.id;

                      return (
                        <SortableEventItem
                          key={event.id}
                          id={event.id}
                          event={event}
                          tagInfo={tagInfo}
                          isDeleting={isDeleting}
                          isEditing={editingEvent?.id === event.id}
                          onEdit={() => startEditEvent(event)}
                          onDelete={() => handleDeleteEvent(event.id)}
                          onRestore={() => handleRestoreEvent(event.id)}
                          isRestoring={isRestoring}
                          onHardDelete={() => setHardDeleteEventConfirm(event)}
                          isHardDeleting={isHardDeleting}
                          isReordering={isReorderingEvents}
                          isProposalContext={isProposalContext}
                          onSaveToLibrary={
                            isProposalContext && event.is_active
                              ? () => handleSaveEventToLibrary(event.id)
                              : undefined
                          }
                          isSavingToLibrary={savingEventToLibraryId === event.id}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            {/* Show/Hide archived toggle for events */}
            {archivedEventsCount > 0 && visibleEvents.length > 0 && (
              <div className="pt-2 flex justify-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowArchivedEvents(!showArchivedEvents)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {showArchivedEvents
                    ? `Hide archived (${archivedEventsCount})`
                    : `Show archived (${archivedEventsCount})`}
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Unsplash Image Search Modal */}
      {isUnsplashSearchOpen && existingContent?.id && (
        <UnsplashImageSearch
          city={city}
          country={country}
          destinationContentId={existingContent.id}
          onImagesAdded={handleUnsplashImagesAdded}
          onClose={() => setIsUnsplashSearchOpen(false)}
        />
      )}

      {/* Event Library Browser */}
      <EventLibraryBrowser
        isOpen={eventLibraryOpen}
        onClose={() => setEventLibraryOpen(false)}
        city={city}
        country={country}
        onEventAdded={async () => {
          await loadEvents();
          onActivitiesChanged?.();
        }}
      />

      {/* Activity Library Browser */}
      <ActivityLibraryBrowser
        isOpen={showActivityLibraryBrowser}
        onClose={() => setShowActivityLibraryBrowser(false)}
        city={city}
        country={country}
        researchDestinationId={isProposalContext ? researchDestinationId : undefined}
        onActivityAdded={async () => {
          await loadActivities();
          onActivitiesChanged?.();
        }}
      />

      {/* Permanent delete confirmation — Event */}
      <ConfirmDialog
        isOpen={!!hardDeleteEventConfirm}
        onClose={() => setHardDeleteEventConfirm(null)}
        onConfirm={() => {
          if (hardDeleteEventConfirm) handleHardDeleteEvent(hardDeleteEventConfirm.id);
        }}
        title="Permanently delete event?"
        description={
          hardDeleteEventConfirm
            ? `"${hardDeleteEventConfirm.name}" will be removed from the library. This cannot be undone.`
            : ''
        }
        confirmText="Delete permanently"
        cancelText="Cancel"
        variant="destructive"
        isLoading={!!hardDeleteEventConfirm && hardDeletingEventId === hardDeleteEventConfirm.id}
      />

      {/* Permanent delete confirmation — Activity */}
      <ConfirmDialog
        isOpen={!!hardDeleteActivityConfirm}
        onClose={() => setHardDeleteActivityConfirm(null)}
        onConfirm={() => {
          if (hardDeleteActivityConfirm) handleHardDeleteActivity(hardDeleteActivityConfirm.id);
        }}
        title="Permanently delete activity?"
        description={
          hardDeleteActivityConfirm
            ? `"${hardDeleteActivityConfirm.name}" will be removed from the library. This cannot be undone.`
            : ''
        }
        confirmText="Delete permanently"
        cancelText="Cancel"
        variant="destructive"
        isLoading={!!hardDeleteActivityConfirm && hardDeletingActivityId === hardDeleteActivityConfirm.id}
      />
    </Modal>
  );
}

// Sortable Image Item Component
interface SortableImageItemProps {
  id: string;
  image: ImageObject;
  city: string;
  index: number;
  onRemove: () => void;
}

function SortableImageItem({ id, image, city, index, onRemove }: SortableImageItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative aspect-square bg-gray-100 rounded-lg overflow-hidden group ${
        isDragging ? 'z-50 cursor-grabbing' : 'cursor-grab'
      }`}
    >
      <div
        {...attributes}
        {...listeners}
        className="absolute inset-0 cursor-grab active:cursor-grabbing z-0"
        title="Drag to reorder"
      />
      <img 
        src={image.url} 
        alt={`${city} ${index + 1}`}
        className="w-full h-full object-cover pointer-events-none"
        onError={(e) => {
          (e.target as HTMLImageElement).src = '/gallery/placeholder.svg';
        }}
      />
      <div
        className="absolute top-1 left-1 p-1 bg-black/60 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10"
        title="Drag to reorder"
      >
        <GripVertical className="w-3 h-3 text-white" />
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onRemove();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        className="absolute top-1 right-1 p-1 bg-red-600 hover:bg-red-700 rounded opacity-0 group-hover:opacity-100 transition-opacity z-20 flex items-center justify-center"
        title="Delete image"
      >
        <Trash2 className="w-3 h-3 text-white" />
      </button>
      {isDragging && (
        <div className="absolute inset-0 border-2 border-primary-500 border-dashed rounded-lg bg-primary-50/20 pointer-events-none z-30" />
      )}
    </div>
  );
}

// Sortable Activity Item Component
interface SortableActivityItemProps {
  id: string;
  activity: DestinationActivity;
  catInfo: { value: ActivityCategory; label: string; emoji: string } | undefined;
  intInfo: { value: ActivityIntensity; label: string; color: string } | undefined;
  isDeleting: boolean;
  isEditing: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onRestore: () => void;
  isRestoring: boolean;
  onHardDelete?: () => void;
  isHardDeleting?: boolean;
  isReordering: boolean;
  isProposalContext?: boolean;
  onPromoteToDestination?: () => void;
  isPromoting?: boolean;
  hasDestinationLink?: boolean; // True if this activity is linked to a destination-pool activity
  onSaveToLibrary?: () => void;
  isSavingToLibrary?: boolean;
}

function SortableActivityItem({
  id,
  activity,
  catInfo,
  intInfo,
  isDeleting,
  isEditing,
  onEdit,
  onDelete,
  onRestore,
  isRestoring,
  onHardDelete,
  isHardDeleting = false,
  isReordering,
  isProposalContext = false,
  onPromoteToDestination,
  isPromoting = false,
  hasDestinationLink = false,
  onSaveToLibrary,
  isSavingToLibrary = false,
}: SortableActivityItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-3 border rounded-lg group relative ${
        activity.is_active ? 'bg-white' : 'bg-gray-50 opacity-60'
      } ${isEditing ? 'border-primary-500' : ''} ${
        isDragging ? 'z-50 cursor-grabbing shadow-lg' : 'cursor-grab'
      } ${isReordering ? 'pointer-events-none' : ''}`}
    >
      <div className="flex items-start gap-2 sm:gap-3">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded hover:bg-gray-100 transition-colors cursor-grab active:cursor-grabbing flex-shrink-0 mt-1"
          title="Drag to reorder"
        >
          <GripVertical className="w-4 h-4 text-gray-400" />
        </div>

        <div className="flex-1 min-w-0 flex gap-2 sm:gap-3">
          {activity.images && activity.images.length > 0 && activity.images[0]?.url && (
            <div className="w-12 h-12 sm:w-20 sm:h-20 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
              <img
                src={activity.images[0].url}
                alt={activity.name}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-1">
              <div className="min-w-0 flex-1">
                <h4 className="font-medium text-sm sm:text-base line-clamp-2 sm:truncate">{activity.name}</h4>
                {!activity.is_active && (
                  <Badge variant="secondary" className="text-xs mt-0.5">Archived</Badge>
                )}
              </div>
              <div className="flex items-center gap-0.5 flex-shrink-0 -mt-0.5">
                {isProposalContext && onPromoteToDestination && !hasDestinationLink && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPromoteToDestination();
                    }}
                    disabled={isDeleting || isReordering || isPromoting}
                    isLoading={isPromoting}
                    className="h-7 w-7 p-0"
                    title="Save to destination library"
                  >
                    {isPromoting ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <ArrowUp className="w-3.5 h-3.5 text-primary-600" />
                    )}
                  </Button>
                )}
                {isProposalContext && hasDestinationLink && (
                  <div
                    className="h-7 w-7 p-0 flex items-center justify-center"
                    title="This activity is linked to the destination library"
                  >
                    <Database className="w-3.5 h-3.5 text-gray-400" />
                  </div>
                )}
                {isProposalContext && onSaveToLibrary && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSaveToLibrary();
                    }}
                    disabled={isDeleting || isReordering || isSavingToLibrary}
                    isLoading={isSavingToLibrary}
                    className="h-7 w-7 p-0"
                    title="Save to global activity library"
                  >
                    {isSavingToLibrary ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <BookmarkPlus className="w-3.5 h-3.5 text-accent-600" />
                    )}
                  </Button>
                )}
                {!activity.is_active && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRestore();
                    }}
                    disabled={isDeleting || isRestoring || isHardDeleting || isReordering}
                    className="h-7 w-7 p-0 text-primary-600 hover:text-primary-700"
                    title={isProposalContext ? 'Include activity' : 'Restore activity (un-archive)'}
                  >
                    {isRestoring ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RotateCcw className="w-3.5 h-3.5" />
                    )}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onEdit}
                  disabled={isDeleting || isRestoring || isHardDeleting || isReordering}
                  className="h-7 w-7 p-0"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                </Button>
                {activity.is_active && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onDelete}
                    disabled={isDeleting || isRestoring || isHardDeleting || isReordering}
                    className="text-error-600 hover:text-error-700 h-7 w-7 p-0"
                    title={isProposalContext ? 'Exclude activity' : 'Archive activity'}
                  >
                    {isDeleting ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" />
                    )}
                  </Button>
                )}
                {/* Hard delete is only meaningful in the global library.
                    In proposal context, the regular delete is already a hard delete. */}
                {!isProposalContext && onHardDelete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onHardDelete();
                    }}
                    disabled={isDeleting || isRestoring || isHardDeleting || isReordering}
                    className="text-error-700 hover:text-white hover:bg-error-600 h-7 w-7 p-0"
                    title="Delete permanently (cannot be undone)"
                  >
                    {isHardDeleting ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash className="w-3.5 h-3.5" />
                    )}
                  </Button>
                )}
              </div>
            </div>

            {activity.description && (
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1 line-clamp-2">
                {activity.description}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-0.5 sm:gap-y-1 mt-1.5 sm:mt-2 text-xs sm:text-sm">
              {catInfo && (
                <span className="flex items-center gap-1">
                  <span>{catInfo.emoji}</span>
                  <span className="text-muted-foreground">{catInfo.label}</span>
                </span>
              )}
              {intInfo && (
                <span className={`${intInfo.color} uppercase text-xs font-medium`}>
                  {intInfo.label}
                </span>
              )}
              {activity.duration_hours && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  {activity.duration_hours}h
                </span>
              )}
              {activity.estimated_price_per_person && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <DollarSign className="w-3 h-3" />
                  {activity.estimated_price_per_person}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {isDragging && (
        <div className="absolute inset-0 border-2 border-primary-500 border-dashed rounded-lg bg-primary-50/20 pointer-events-none z-30" />
      )}
    </div>
  );
}

// Sortable Event Item Component
interface SortableEventItemProps {
  id: string;
  event: DestinationEvent;
  tagInfo: { value: EventTag; label: string; emoji: string } | undefined;
  isDeleting: boolean;
  isEditing: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onRestore: () => void;
  isRestoring: boolean;
  onHardDelete: () => void;
  isHardDeleting: boolean;
  isReordering: boolean;
  /** Matches SortableActivityItem: in a proposal the trash means "exclude", not "archive". */
  isProposalContext: boolean;
  onSaveToLibrary?: () => void;
  isSavingToLibrary?: boolean;
}

function SortableEventItem({
  id,
  event,
  tagInfo,
  isDeleting,
  isEditing,
  onEdit,
  onDelete,
  onRestore,
  isRestoring,
  onHardDelete,
  isHardDeleting,
  isReordering,
  isProposalContext,
  onSaveToLibrary,
  isSavingToLibrary = false,
}: SortableEventItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-3 border rounded-lg group relative ${
        event.is_active ? 'bg-white' : 'bg-gray-50 opacity-60'
      } ${isEditing ? 'border-primary-500' : ''} ${
        isDragging ? 'z-50 cursor-grabbing shadow-lg' : 'cursor-grab'
      } ${isReordering ? 'pointer-events-none' : ''}`}
    >
      <div className="flex items-start justify-between gap-3">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-100 transition-colors cursor-grab active:cursor-grabbing flex-shrink-0"
          title="Drag to reorder"
        >
          <GripVertical className="w-4 h-4 text-gray-400" />
        </div>
        
        <div className="flex-1 min-w-0 flex gap-3">
          {event.images && event.images.length > 0 && event.images[0]?.url && (
            <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
              <img 
                src={event.images[0].url} 
                alt={event.name}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-medium truncate">{event.name}</h4>
              {!event.is_active && (
                <Badge variant="secondary" className="text-xs">Archived</Badge>
              )}
            </div>
            
            {event.description && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {event.description}
              </p>
            )}
            
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm">
              {tagInfo && (
                <span className="flex items-center gap-1">
                  <span>{tagInfo.emoji}</span>
                  <span className="text-muted-foreground">{tagInfo.label}</span>
                </span>
              )}
              {event.participants && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Users className="w-3 h-3" />
                  <span>{event.participants}</span>
                </span>
              )}
              {event.estimated_price_per_person && (
                <span className="text-muted-foreground">
                  ${event.estimated_price_per_person}/person
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {!event.is_active && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRestore}
              disabled={isDeleting || isRestoring || isHardDeleting || isReordering}
              className="text-primary-600 hover:text-primary-700 hover:bg-primary-50"
              title="Restore event (un-archive)"
            >
              {isRestoring ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4" />
              )}
            </Button>
          )}
          {onSaveToLibrary && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onSaveToLibrary}
              disabled={isDeleting || isRestoring || isHardDeleting || isReordering || isSavingToLibrary}
              isLoading={isSavingToLibrary}
              className="h-7 w-7 p-0"
              title="Save to global event library"
            >
              {isSavingToLibrary ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <BookmarkPlus className="w-3.5 h-3.5 text-accent-600" />
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            disabled={isDeleting || isRestoring || isHardDeleting || isReordering}
            title="Edit event"
          >
            <Edit3 className="w-4 h-4" />
          </Button>
          {event.is_active && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              disabled={isDeleting || isRestoring || isHardDeleting || isReordering}
              className="text-error-600 hover:text-error-700 hover:bg-error-50"
              title={isProposalContext ? 'Exclude event' : 'Archive event'}
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onHardDelete}
            disabled={isDeleting || isRestoring || isHardDeleting || isReordering}
            className="text-error-700 hover:text-white hover:bg-error-600"
            title="Delete permanently (cannot be undone)"
          >
            {isHardDeleting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>

      {isDragging && (
        <div className="absolute inset-0 border-2 border-primary-500 border-dashed rounded-lg bg-primary-50/20 pointer-events-none z-30" />
      )}
    </div>
  );
}

